import type { ReactNode } from "react";
import { getFilenameFromPath } from "@/lib/utils";
import {
  isGroupedAttachments,
  hasAnyFiles,
  type Attachments,
  type FileMetadata,
} from "@/modules/agreement/utils";
import {
  AttachmentsDisplaySection,
  type AttachmentGroup,
  type AttachmentFileItem,
  type AttachmentFileUrlResolver,
} from "@/components/AttachmentsDisplaySection";

interface AgreementAttachmentsDisplayProps {
  attachments: Attachments;
  title?: string;
  attachmentType?: string;
  canRead?: boolean;
  /** Adres na żądanie (endpoint rejestrujący pobrania); bez niego adres bezpośredni jak dotąd */
  resolveFileUrl?: AttachmentFileUrlResolver;
  /** Dodatkowa linia pod nazwą pliku */
  renderFileMeta?: (file: AttachmentFileItem) => ReactNode;
  /** Dodatkowe akcje w wierszu pliku */
  renderFileActions?: (file: AttachmentFileItem) => ReactNode;
}

function toAttachmentFileItem(
  file: FileMetadata,
  fileIndex: number,
): AttachmentFileItem {
  return {
    filename: getFilenameFromPath(file.path, `file_${fileIndex + 1}`),
    path: file.path,
    size: file.size,
    confirmed_at: file.confirmed_at,
  };
}

function toGroups(attachments: Attachments, title?: string): AttachmentGroup[] {
  if (isGroupedAttachments(attachments)) {
    return attachments.map((group) => ({
      key: group.key,
      title: group.title,
      files: group.files.map((file, i) => toAttachmentFileItem(file, i)),
    }));
  }
  const simple = attachments as FileMetadata[];
  return [
    {
      key: "agreement-files",
      title: title ?? "",
      files: simple.map((file, i) => toAttachmentFileItem(file, i)),
    },
  ];
}

export const AgreementAttachmentsDisplay = ({
  attachments,
  title,
  attachmentType,
  canRead = true,
  resolveFileUrl,
  renderFileMeta,
  renderFileActions,
}: AgreementAttachmentsDisplayProps) => {
  const groups = hasAnyFiles(attachments) ? toGroups(attachments, title) : [];

  return (
    <AttachmentsDisplaySection
      groups={groups}
      attachmentType={attachmentType}
      canRead={canRead}
      resolveFileUrl={resolveFileUrl}
      renderFileMeta={renderFileMeta}
      renderFileActions={renderFileActions}
    />
  );
};
